import { hourThresholdForAggregations } from '@semios/app-platform-values-helpers-and-types'
import { getMomentDateMax } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/getMomentDateMax'
import { getMomentDateMin } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/getMomentDateMin'
import type { TDataExportStoreState } from 'App/data-export/store/dataExportStore'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'

export const validateDateRangeAndInterval = (
  s: TDataExportStoreState,
): {
  interval: TDataExportStoreState['formErrors']['interval']
  dateRange: TDataExportStoreState['formErrors']['dateRange']
} => {
  const propertyTimezone =
    fieldAssetStore.getState().properties?.[Number(s.formState.propertyId)].timezone ?? moment.tz.guess()

  const intervalError =
    s.formState.interval === null ? translate.phrases.banyanApp('Please select an interval') : null

  let dateRangeError = null

  const mDateFrom = s.formState.dateRange[0]
    ? moment.tz(s.formState.dateRange[0], propertyTimezone).startOf('day')
    : null

  const mDateTo = s.formState.dateRange[1]
    ? moment.tz(s.formState.dateRange[1], propertyTimezone).endOf('day')
    : null

  const mDateMin = getMomentDateMin(propertyTimezone)
  const mDateMax = getMomentDateMax(propertyTimezone)

  if (!mDateFrom || !mDateTo) {
    dateRangeError = translate.phrases.banyanApp('Please select a date range')
  } else if (mDateFrom.isBefore(mDateMin, 'days')) {
    dateRangeError = translate.phrases.validation('{{label}} must be >= {{value}}', {
      label: translate.phrases.banyanApp('Start Date'),
      value: translate.dates.format(mDateMin, 'MMM D, YYYY'),
    })
  } else if (mDateTo.isAfter(mDateMax, 'days')) {
    dateRangeError = translate.phrases.validation('{{label}} must be <= {{value}}', {
      label: translate.phrases.banyanApp('End Date'),
      value: translate.dates.format(mDateMax, 'MMM D, YYYY'),
    })
  } else if (s.formState.interval === 'HOURLY') {
    const maxDays = Math.floor(hourThresholdForAggregations.HOURLY / 24)

    if (mDateTo.diff(mDateFrom, 'days', true) > maxDays) {
      dateRangeError = translate.phrases.banyanApp(
        'Date range cannot be more than {{maxDays}} days for hourly data',
        { maxDays: maxDays },
      )
    }
  } else if (s.formState.interval === 'UNAGGREGATED') {
    const maxDays = Math.floor(hourThresholdForAggregations.UNAGGREGATED / 24)

    if (mDateTo.diff(mDateFrom, 'days', true) > maxDays) {
      dateRangeError = translate.phrases.banyanApp(
        'Date range cannot be more than {{maxDays}} days for ten-minutely data',
        { maxDays: maxDays },
      )
    }
  } else if (s.formState.interval === 'DAILY') {
    const maxYears = 2

    if (mDateTo.clone().startOf('day').diff(mDateFrom, 'years', true) > maxYears) {
      dateRangeError = translate.phrases.banyanApp(
        'Date range cannot be more than {{maxYears}} years for daily data',
        { maxYears: maxYears },
      )
    }
  }

  return {
    interval: intervalError,
    dateRange: dateRangeError,
  }
}
